import React, { useEffect, useState } from 'react';
import { configApi, PremiumConfig as PremiumConfigType } from '../../../api/config';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Spinner } from '../../../components/ui/Spinner';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import { useAuth } from '../../../hooks/useAuth';

export const PremiumConfig: React.FC = () => {
  const { admin } = useAuth();
  const [config, setConfig] = useState<PremiumConfigType | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingField, setEditingField] = useState<'fee' | 'trial' | null>(null);
  const [editValue, setEditValue] = useState('');
  const [pendingUpdate, setPendingUpdate] = useState<{
    field: 'fee' | 'trial';
    value: number;
  } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canEdit = admin && (admin.role === 'super_admin' || admin.role === 'finance');

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const data = await configApi.getPremiumConfig();
      setConfig(data);
    } catch {
      setError('Failed to load premium configuration.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchConfig();
  }, []);

  const handleEdit = (field: 'fee' | 'trial') => {
    if (!config) return;
    setEditingField(field);
    setEditValue(field === 'fee' ? config.monthlyFee.toString() : config.trialDays.toString());
  };

  const handleSave = (field: 'fee' | 'trial') => {
    const value = parseFloat(editValue);
    if (isNaN(value) || value < 0) return;
    setPendingUpdate({ field, value });
    setEditingField(null);
  };

  const executeUpdate = async () => {
    if (!pendingUpdate) return;
    try {
      setActionLoading(true);
      if (pendingUpdate.field === 'fee') {
        await configApi.updatePremiumFee(pendingUpdate.value);
      } else {
        await configApi.updatePremiumTrial(pendingUpdate.value);
      }
      await fetchConfig();
      setPendingUpdate(null);
    } catch {
      setError('Failed to update premium configuration.');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner size={24} />
      </div>
    );
  }

  const inputStyle: React.CSSProperties = {
    width: 120,
    height: 36,
    padding: '0 10px',
    fontSize: 14,
    fontFamily: 'var(--font-family)',
    color: 'var(--neutral-800)',
    background: 'var(--neutral-100)',
    border: '1px solid var(--blue-600)',
    borderRadius: 'var(--radius-md)',
    outline: 'none',
  };

  const rows: {
    label: string;
    description: string;
    field: 'fee' | 'trial';
    value: string;
  }[] = config
    ? [
        {
          label: 'Monthly Fee',
          description: 'Amount charged per month for Premium subscription',
          field: 'fee',
          value: `₦${config.monthlyFee.toLocaleString('en-NG')}`,
        },
        {
          label: 'Trial Duration',
          description: 'Number of free trial days for new subscribers',
          field: 'trial',
          value: `${config.trialDays} day${config.trialDays !== 1 ? 's' : ''}`,
        },
      ]
    : [];

  return (
    <>
      <Card padding={0}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--neutral-300)' }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--neutral-800)' }}>
            Premium Subscription
          </h3>
          <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 2 }}>
            Subscription pricing and trial period configuration
          </p>
        </div>

        {error && (
          <div
            style={{
              margin: 20,
              padding: 12,
              background: 'var(--danger-100)',
              color: 'var(--danger-900)',
              fontSize: 13,
              borderRadius: 'var(--radius-md)',
            }}
          >
            {error}
          </div>
        )}

        <div>
          {rows.map((row, i) => (
            <div
              key={row.field}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(min(100%, 220px), 1fr) auto auto',
                gap: 16,
                padding: '16px 20px',
                alignItems: 'center',
                borderBottom: i < rows.length - 1 ? '1px solid var(--neutral-200)' : 'none',
                background: i % 2 === 0 ? 'var(--neutral-100)' : 'var(--neutral-200)',
              }}
            >
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--neutral-800)' }}>
                  {row.label}
                </div>
                <div style={{ fontSize: 12, color: 'var(--neutral-500)', marginTop: 2 }}>
                  {row.description}
                </div>
              </div>

              {editingField === row.field ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="number"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    autoFocus
                    style={inputStyle}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSave(row.field);
                      if (e.key === 'Escape') setEditingField(null);
                    }}
                  />
                  <Button size="sm" onClick={() => handleSave(row.field)}>Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingField(null)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <span
                  style={{
                    fontSize: 15,
                    fontWeight: 600,
                    color: 'var(--neutral-800)',
                    fontFamily: 'monospace',
                  }}
                >
                  {row.value}
                </span>
              )}

              {canEdit && editingField !== row.field && (
                <Button size="sm" variant="secondary" onClick={() => handleEdit(row.field)}>
                  Edit
                </Button>
              )}
            </div>
          ))}
        </div>
      </Card>

      <ConfirmDialog
        isOpen={pendingUpdate !== null}
        title={pendingUpdate?.field === 'fee' ? 'Update monthly fee?' : 'Update trial duration?'}
        message={
          pendingUpdate?.field === 'fee'
            ? `New subscribers will be charged ₦${pendingUpdate?.value.toLocaleString()} per month. Existing subscriptions are not affected.`
            : `New subscribers will receive a ${pendingUpdate?.value}-day free trial. Existing trials are not affected.`
        }
        confirmLabel="Update"
        variant="primary"
        loading={actionLoading}
        onConfirm={executeUpdate}
        onCancel={() => setPendingUpdate(null)}
      />
    </>
  );
};
